import { useEffect, useMemo, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import {
  Badge,
  Button,
  Form,
  Modal,
  Table,
  Dropdown,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import '../../styles/admin.css';
import './AdminOrdersPage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatPrice = (price, currency = 'ARS') => {
  if (!price && price !== 0) return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(0);
  const numPrice = parseFloat(price);
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currency || 'ARS'
  }).format(numPrice);
};

const getStatusBadge = (status) => {
  const statusMap = {
    'pending': { label: 'Pendiente', variant: 'warning' },
    'processing': { label: 'Procesando', variant: 'info' },
    'paid': { label: 'Pagado', variant: 'success' },
    'completed': { label: 'Completado', variant: 'success' },
    'canceled': { label: 'Cancelado', variant: 'danger' },
    'refunded': { label: 'Reembolsado', variant: 'secondary' },
    'failed': { label: 'Fallido', variant: 'danger' },
  };
  const statusInfo = statusMap[status] || { label: status, variant: 'secondary' };
  return <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>;
};

const getFulfillmentBadge = (status) => {
  if (!status) return null;
  const statusLower = status.toLowerCase();
  let variant = 'secondary';
  let icon = 'bi-box';
  
  if (statusLower.includes('tomando') || statusLower.includes('preparando')) {
    variant = 'warning';
    icon = 'bi-hourglass-split';
  } else if (statusLower.includes('despachado') || statusLower.includes('enviado')) {
    variant = 'info';
    icon = 'bi-truck';
  } else if (statusLower.includes('camino') || statusLower.includes('transito')) {
    variant = 'primary';
    icon = 'bi-geo-alt';
  } else if (statusLower.includes('entregado') || statusLower.includes('recibido')) {
    variant = 'success';
    icon = 'bi-check-circle';
  }
  
  return (
    <Badge bg={variant} className="d-flex align-items-center gap-1 order-fulfillment-badge">
      <i className={`bi ${icon}`} aria-hidden="true" />
      {status}
    </Badge>
  );
};

const getProductTypeLabel = (type) => {
  const typeMap = {
    'course': 'Curso',
    'event': 'Evento',
    'item': 'Item',
    'items': 'Item',
    'physical': 'Item',
    'asesoria': 'Asesoría',
    'consult': 'Asesoría'
  };
  return typeMap[type] || 'Producto';
};

const isPhysicalLine = (item) =>
  item.productType === 'item' || item.productType === 'items' || item.productType === 'physical';

const fulfillmentRank = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('tomando') || s.includes('preparando')) return 0;
  if (s.includes('despachado') || s.includes('enviado')) return 1;
  if (s.includes('camino') || s.includes('transito')) return 2;
  if (s.includes('entregado') || s.includes('recibido')) return 3;
  return 0;
};

/** Un solo estado logístico por pedido (vista admin). */
const resolveOrderFulfillment = (items) => {
  const physical = (items || []).filter((it) => isPhysicalLine(it) && it.fulfillmentStatus);
  if (physical.length === 0) return null;

  const uniqueStatuses = [...new Set(physical.map((it) => String(it.fulfillmentStatus).trim()))];
  const status = uniqueStatuses.reduce((prev, curr) =>
    fulfillmentRank(prev) <= fulfillmentRank(curr) ? prev : curr,
  );

  const trackingNumbers = [...new Set(physical.map((it) => it.trackingNumber).filter(Boolean))];

  return {
    status,
    trackingNumber:
      trackingNumbers.length === 1
        ? trackingNumbers[0]
        : trackingNumbers.length > 1
          ? trackingNumbers.join(' · ')
          : null,
    physicalCount: physical.length,
  };
};

const AdminOrdersPageInner = () => {
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();
  const navigate = useNavigate();

  // Verificar permisos
  const hasViewOrders = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_PEDIDOS), [user]);
  const hasManageOrders = useMemo(() => hasPermission(user, PERMISSION_IDS.GESTIONAR_PEDIDOS), [user]);
  const hasUpdateFulfillment = useMemo(() => hasPermission(user, PERMISSION_IDS.ACTUALIZAR_ESTADO_PEDIDO), [user]);

  // Si no tiene permiso, redirigir
  useEffect(() => {
    if (user && !hasViewOrders && !hasManageOrders) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para acceder a la gestión de pedidos.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, hasViewOrders, hasManageOrders, navigate, notify]);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showFulfillmentModal, setShowFulfillmentModal] = useState(false);
  const [fulfillmentOrder, setFulfillmentOrder] = useState(null);
  const [fulfillmentStatus, setFulfillmentStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [updating, setUpdating] = useState(false);
  const [filter, setFilter] = useState('all'); // all, physical, pending

  const fetchOrders = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/orders/admin/list'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error al cargar pedidos (${response.status})`);
      }

      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      logError('[AdminOrdersPage] Error fetching orders:', error);
      notify({
        title: 'Error al cargar pedidos',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [token]);

  const closeFulfillmentModal = () => {
    setShowFulfillmentModal(false);
    setFulfillmentOrder(null);
    setFulfillmentStatus('');
    setTrackingNumber('');
  };

  const handleUpdateFulfillment = async () => {
    if (!fulfillmentOrder || !fulfillmentStatus) return;

    const physicalItems = (fulfillmentOrder.items || []).filter(isPhysicalLine);
    if (physicalItems.length === 0) return;

    setUpdating(true);
    try {
      const response = await fetch(
        buildApiUrl(`/api/orders/${fulfillmentOrder.id}/fulfillment`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: fulfillmentStatus,
            trackingNumber: trackingNumber || null,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al actualizar estado');
      }

      const data = await response.json().catch(() => ({}));
      const emailNote =
        data.emailStatus?.buyerSent || data.emailStatus?.staffSent
          ? ' Se notificó al comprador por correo.'
          : data.emailStatus?.configured === false
            ? ' (SMTP no configurado: sin correos.)'
            : '';

      notify({
        title: 'Estado actualizado',
        message: `Estado de envío actualizado para el pedido.${emailNote}`,
        variant: 'success',
      });

      closeFulfillmentModal();
      fetchOrders();
    } catch (error) {
      logError('[AdminOrdersPage] Error updating fulfillment:', error);
      notify({
        title: 'Error al actualizar',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setUpdating(false);
    }
  };

  const openOrderFulfillmentModal = (order) => {
    const summary = resolveOrderFulfillment(order.items);
    setFulfillmentOrder(order);
    setFulfillmentStatus(summary?.status || 'Tomando Pedido');
    setTrackingNumber(summary?.trackingNumber?.split(' · ')[0] || '');
    setShowFulfillmentModal(true);
  };

  const filteredOrders = useMemo(() => {
    if (filter === 'all') return orders;
    if (filter === 'physical') {
      return orders.filter(order => order.physicalItemsCount > 0);
    }
    if (filter === 'pending') {
      return orders.filter(order => order.hasPendingPhysicalItems);
    }
    return orders;
  }, [orders, filter]);

  const stats = useMemo(() => {
    const total = orders.length;
    const physical = orders.filter(o => o.physicalItemsCount > 0).length;
    const pending = orders.filter(o => o.hasPendingPhysicalItems).length;
    const paid = orders.filter(o => o.status === 'paid' || o.status === 'completed').length;
    return { total, physical, pending, paid };
  }, [orders]);

  if (loading) {
    return (
      <div className="admin-page">
        <AdminSidebar
          activeKey="orders"
          onNavigate={(item) => {
            if (item.path) {
              navigate(item.path);
            }
          }}
        />
        <div className="admin-content">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-3">Cargando pedidos...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <AdminSidebar
        activeKey="orders"
        onNavigate={(item) => {
          if (item.path) {
            navigate(item.path);
          }
        }}
      />
      <div className="admin-content">
        <div className="admin-header">
          <h1 className="admin-title">
            <i className="bi bi-bag-check me-2"></i>
            Gestión de Pedidos
          </h1>
          <p className="admin-subtitle">
            Administra todos los pedidos y estados de cumplimiento
          </p>
        </div>

        {/* Estadísticas */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="stat-card stat-card--blue">
              <div className="stat-icon">
                <i className="bi bi-bag-check"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.total}</div>
                <div className="stat-label">Total Pedidos</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stat-card stat-card--orange">
              <div className="stat-icon">
                <i className="bi bi-box-seam"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.physical}</div>
                <div className="stat-label">Con Items Físicos</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stat-card stat-card--yellow">
              <div className="stat-icon">
                <i className="bi bi-hourglass-split"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.pending}</div>
                <div className="stat-label">Pendientes</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stat-card stat-card--green">
              <div className="stat-icon">
                <i className="bi bi-check-circle"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.paid}</div>
                <div className="stat-label">Pagados</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div className="btn-group" role="group">
            <Button
              variant={filter === 'all' ? 'primary' : 'outline-primary'}
              onClick={() => setFilter('all')}
            >
              Todos
            </Button>
            <Button
              variant={filter === 'physical' ? 'primary' : 'outline-primary'}
              onClick={() => setFilter('physical')}
            >
              Con Items Físicos
            </Button>
            <Button
              variant={filter === 'pending' ? 'primary' : 'outline-primary'}
              onClick={() => setFilter('pending')}
            >
              Pendientes
            </Button>
          </div>
          <Button variant="outline-secondary" onClick={fetchOrders}>
            <i className="bi bi-arrow-clockwise me-2"></i>
            Actualizar
          </Button>
        </div>

        {/* Tabla de pedidos */}
        {filteredOrders.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-bag-x empty-icon"></i>
            <h5 className="empty-title">No hay pedidos</h5>
            <p className="empty-description">
              {filter === 'physical' 
                ? 'No hay pedidos con items físicos.' 
                : filter === 'pending'
                ? 'No hay pedidos pendientes.'
                : 'Aún no se han realizado pedidos.'}
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="orders-table">
              <thead>
                <tr>
                  <th>Orden</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Productos</th>
                  <th>Total</th>
                  <th>Estado Pago</th>
                  <th>Items Físicos</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => {
                  const orderFulfillment = resolveOrderFulfillment(order.items);
                  return (
                    <Fragment key={order.id}>
                      <tr
                        className={expandedOrder === order.id ? 'table-active' : ''}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                      >
                        <td>
                          <strong>{order.orderNumber}</strong>
                        </td>
                        <td>
                          {order.user ? (
                            <div>
                              <div>{order.user.nombre} {order.user.apellido}</div>
                              <small className="text-muted">{order.user.email}</small>
                            </div>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td>{formatDate(order.created_at)}</td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            {order.items && order.items.slice(0, 2).map((item, idx) => (
                              <div key={item.id ?? idx} className="d-flex align-items-center gap-2">
                                {item.productImage ? (
                                  <img
                                    src={resolveImageUrl(item.productImage)}
                                    alt={item.productName}
                                    className="product-thumbnail"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="product-thumbnail-placeholder">
                                    <i className="bi bi-box"></i>
                                  </div>
                                )}
                                <div>
                                  <div className="product-name">{item.productName}</div>
                                  <Badge bg="secondary" className="product-type-badge">
                                    {getProductTypeLabel(item.productType)}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                            {order.items && order.items.length > 2 && (
                              <small className="text-muted">
                                +{order.items.length - 2} más
                              </small>
                            )}
                          </div>
                        </td>
                        <td>
                          <strong>{formatPrice(order.totalAmount, order.currency)}</strong>
                        </td>
                        <td>{getStatusBadge(order.status)}</td>
                        <td>
                          {order.physicalItemsCount > 0 ? (
                            <Badge bg="info">{order.physicalItemsCount} items</Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedOrder(expandedOrder === order.id ? null : order.id);
                            }}
                          >
                            {expandedOrder === order.id ? (
                              <>
                                <i className="bi bi-chevron-up me-1"></i>
                                Ocultar
                              </>
                            ) : (
                              <>
                                <i className="bi bi-chevron-down me-1"></i>
                                Ver Detalles
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                      {expandedOrder === order.id && (
                        <tr>
                          <td colSpan="8" className="order-details-cell">
                            <div className="order-details">
                              <div className="order-details-top row g-3">
                                <div className="col-lg-5">
                                  <div className="order-meta-panel">
                                    <h6 className="order-panel-title">
                                      <i className="bi bi-receipt me-2" aria-hidden="true" />
                                      Resumen del pedido
                                    </h6>
                                    <ul className="order-meta-list list-unstyled mb-0">
                                      <li>
                                        <span className="order-meta-label">Nº orden</span>
                                        <span className="order-meta-value">{order.orderNumber}</span>
                                      </li>
                                      <li>
                                        <span className="order-meta-label">Fecha</span>
                                        <span className="order-meta-value">{formatDate(order.created_at)}</span>
                                      </li>
                                      <li>
                                        <span className="order-meta-label">Estado de pago</span>
                                        <span className="order-meta-value">{getStatusBadge(order.status)}</span>
                                      </li>
                                      <li>
                                        <span className="order-meta-label">Total</span>
                                        <span className="order-meta-value order-meta-value--emphasis">
                                          {formatPrice(order.totalAmount, order.currency)}
                                        </span>
                                      </li>
                                      <li>
                                        <span className="order-meta-label">Ítems</span>
                                        <span className="order-meta-value">{order.itemCount ?? order.items?.length ?? 0}</span>
                                      </li>
                                      {orderFulfillment && (
                                        <li className="order-meta-fulfillment-row">
                                          <span className="order-meta-label">Estado del pedido</span>
                                          <span className="order-meta-value order-meta-fulfillment">
                                            {getFulfillmentBadge(orderFulfillment.status)}
                                            {hasUpdateFulfillment && (
                                              <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="order-meta-fulfillment-btn"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  openOrderFulfillmentModal(order);
                                                }}
                                              >
                                                <i className="bi bi-pencil me-1" aria-hidden="true" />
                                                Actualizar estado
                                              </Button>
                                            )}
                                          </span>
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                </div>
                              </div>

                              <h6 className="order-products-title">
                                <i className="bi bi-box-seam me-2" aria-hidden="true" />
                                Productos
                              </h6>
                              <div className="order-products-grid">
                                {order.items && order.items.map((item, idx) => (
                                    <article key={item.id ?? idx} className="order-item-card">
                                      <div className="order-item-card__media">
                                        {item.productImage ? (
                                          <img
                                            src={resolveImageUrl(item.productImage)}
                                            alt=""
                                            className="order-item-card__img"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                        ) : (
                                          <div className="order-item-card__img-placeholder" aria-hidden="true">
                                            <i className="bi bi-box"></i>
                                          </div>
                                        )}
                                      </div>
                                      <div className="order-item-card__body">
                                        <div className="order-item-card__info">
                                          <h6 className="order-item-card__name">{item.productName}</h6>
                                          <div className="order-item-card__chips">
                                            <span className="order-chip">{getProductTypeLabel(item.productType)}</span>
                                            <span className="order-chip order-chip--muted">Cant. {item.quantity}</span>
                                          </div>
                                        </div>
                                        <div className="order-item-card__price-block">
                                          {formatPrice(item.totalPrice, order.currency)}
                                        </div>
                                      </div>
                                    </article>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        {/* Modal para actualizar estado de cumplimiento */}
        <Modal show={showFulfillmentModal} onHide={closeFulfillmentModal}>
          <Modal.Header closeButton>
            <Modal.Title>Actualizar estado del pedido</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {fulfillmentOrder && (
              <>
                <p className="mb-2">
                  <strong>Pedido:</strong> {fulfillmentOrder.orderNumber}
                </p>
                <p className="text-muted small mb-3">
                  El estado y el seguimiento se aplican a los{' '}
                  {(fulfillmentOrder.items || []).filter(isPhysicalLine).length} producto(s) físicos
                  de este pedido.
                </p>
                <Form.Group className="mb-3">
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    value={fulfillmentStatus}
                    onChange={(e) => setFulfillmentStatus(e.target.value)}
                  >
                    <option value="Tomando Pedido">Tomando Pedido</option>
                    <option value="Despachado">Despachado</option>
                    <option value="En Camino">En Camino</option>
                    <option value="Entregado">Entregado</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Número de Seguimiento (Opcional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Ej: ABC123456789"
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeFulfillmentModal}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateFulfillment}
              disabled={!fulfillmentStatus || updating || !fulfillmentOrder}
            >
              {updating ? 'Actualizando...' : 'Actualizar'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

const AdminOrdersPage = () => {
  return (
    <AdminNotificationsProvider>
      <AdminOrdersPageInner />
    </AdminNotificationsProvider>
  );
};

export default AdminOrdersPage;

